import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { styled } from '@mui/material/styles'
import {
  type BasePuiDialogProps,
  ButtonWithLoader,
  type Nil,
  PuiDialog,
} from '@pbt/pbt-ui-components'

import { patientMembershipsSelectors } from '~/store/duck/patientMemberships'
import { getPatientName } from '~/store/reducers/patients'
import type { DataHandle, Membership } from '~/types'

import CancelMembershipDetails, {
  type CancellationData,
} from './CancelMembershipDetails'

const StyledButton = styled(ButtonWithLoader)`
  min-width: 170px;
`

const StyledPuiDialog = styled(PuiDialog)`
  & .MuiDialog-paper {
    width: 650px;
    max-width: 650px;
  }

  & .MuiDialogActions-root {
    padding: ${({ theme }) => theme.spacing(1, 2)};
  }
`

interface CancelMembershipDialogV2Props extends BasePuiDialogProps {
  onCancelMembership?: () => void
  patientId: string | Nil
}

const CancelMembershipDialogV2 = ({
  patientId,
  open,
  onClose,
  onCancelMembership,
}: CancelMembershipDialogV2Props) => {
  const { t } = useTranslation(['Common', 'Memberships'])

  const membership: Membership | Nil = useSelector(
    patientMembershipsSelectors.getItem(patientId),
  )
  const patientName = useSelector(getPatientName(patientId))

  const cancellationDetailsRef = useRef<DataHandle<CancellationData>>()

  const handleClose = () => {
    if (onClose) {
      onClose()
    }
  }

  const handleSubmit = () => {
    if (cancellationDetailsRef.current?.validate()) {
      // TODO: integrate BE API calls https://chewyinc.atlassian.net/browse/CVC-25284
      if (onCancelMembership) {
        onCancelMembership()
      }
      handleClose()
    }
  }

  const planName = membership?.planName || ''
  const membershipInfo = planName ? `${patientName} | ${planName}` : patientName
  const dialogTitle = `${t('Common:CANCEL_ACTION')}: ${membershipInfo}`
  const planEndDate = membership?.plans[0]?.period?.endDate

  return (
    <StyledPuiDialog
      actions={
        <StyledButton onClick={handleSubmit}>
          {t('Common:SUBMIT_ACTION')}
        </StyledButton>
      }
      aria-labelledby="cancel-membership-dialog-v2"
      open={open}
      scroll="paper"
      title={dialogTitle}
      onClose={handleClose}
    >
      <CancelMembershipDetails
        planEndDate={planEndDate}
        ref={cancellationDetailsRef}
      />
    </StyledPuiDialog>
  )
}

export default CancelMembershipDialogV2
